import { Field, Formik, FormikErrors } from 'formik';
import { FormikUtils } from '../../shared/utils';
import { Guid } from '../../shared/models';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import React from 'react';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
} from '../../shared/components';
import { Grid } from '@mui/material';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys } from '../../shared/hooks';
import {
	ESignDocument,
	ESignDocumentReminderFrequency,
	ESignDocumentReminderFrequencyMap,
} from '../../shared/models/ESignDocument';
import { getApiResponseData } from '../../shared/utils/axios';

interface ChangeReminderFrequencyDrawerProps {
	dealGuid: string | undefined;
	eSignDoc: ESignDocument | null | undefined;
	productSubscriptionGuid: Guid | null;
	companySubscriptionGuid: Guid | null;
	companyGuid?: Guid;
	isProductSubscription: boolean;
	onClose: () => void;
	open: boolean;
}

export const ChangeReminderFrequencyDrawer: React.FC<ChangeReminderFrequencyDrawerProps> = ({
	dealGuid,
	eSignDoc,
	productSubscriptionGuid,
	companySubscriptionGuid,
	companyGuid,
	isProductSubscription,
	open,
	onClose,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);

	const updateReminderMutation = useMutation(
		async ({ values }: ChangeReminderFrequencyDrawerMutationVariables) => {
			const endpoint = isProductSubscription
				? CerebroApiEndpoints.Deals.UpdateContractReminder(
						dealGuid,
						productSubscriptionGuid!,
						eSignDoc?.agreementId
				  )
				: CerebroApiEndpoints.Company.UpdateContractReminder(
						companyGuid,
						companySubscriptionGuid!,
						eSignDoc?.agreementId
				  );

			const { data, error } = await getApiResponseData<void>(api.patch(endpoint, values));

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.Root(dealGuid));
				snackbar.enqueueSnackbar('Reminder frequency successfully updated.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading>Change Reminder Frequency</CerebroHeading>

			<Formik
				enableReinitialize
				initialValues={
					{
						comment: '',
						reminderFrequency: 'None',
					} as ChangeReminderFrequencyDrawerValues
				}
				onSubmit={FormikUtils.MutateOnSubmit(updateReminderMutation.mutate)}
			>
				{({ isSubmitting, handleSubmit }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<Grid item xs={12}>
							<CerebroLabel htmlFor="reminderFrequency" sx={{ marginTop: 6 }}>
								Reminder Frequency
							</CerebroLabel>

							<Grid container spacing={3}>
								<Grid item xs={6}>
									<Field
										id="reminderFrequency"
										as={CerebroDropdown}
										name="reminderFrequency"
										disabled={isSubmitting}
									>
										{Object.entries(ESignDocumentReminderFrequencyMap).map(
											([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											)
										)}
									</Field>
								</Grid>
							</Grid>
						</Grid>

						<Grid item xs={12}>
							<CerebroLabel htmlFor="message" sx={{ marginTop: 6 }}>
								Message
							</CerebroLabel>
							<CerebroText variant="hint">
								An optional message to the participants, describing what is being sent or why their
								signature is required.
							</CerebroText>

							<Field rows={4} as={CerebroTextarea} disabled={isSubmitting} id="message" name="message" />
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Send
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type ChangeReminderFrequencyDrawerMutationVariables =
	FormikUtils.MutateOnSubmitVariables<ChangeReminderFrequencyDrawerValues>;
interface ChangeReminderFrequencyDrawerValues {
	comment: string;
	reminderFrequency: ESignDocumentReminderFrequency;
}
